import { apiRequest } from '../../../shared/services/apiClient';
import { normalizeVisitPlace } from '../../field-operations/visitPlace';
import {
  CreateTaskRequest,
  PagedTaskResult,
  TaskActivityItem,
  TaskAttachmentItem,
  TaskCategoryItem,
  TaskCommentItem,
  TaskCommentMentionInput,
  TaskConversationLinkItem,
  TaskLabelItem,
  TaskRelatedTaskLinkItem,
  TaskAiRunStatus,
  TaskItem,
  TaskListQuery,
  TaskPriority,
  TaskRecurrence,
  TaskStatusDefinition,
  TaskStatus,
  UpdateTaskRequest,
  SubtaskItem,
} from '../types/Task';

const priorityByNumber: Record<number, TaskPriority> = {
  0: 'low',
  1: 'medium',
  2: 'high',
  3: 'urgent',
};

const recurrenceByNumber: Record<number, TaskRecurrence> = {
  0: 'doesNotRepeat',
  1: 'daily',
  2: 'weekly',
  3: 'monthly',
  4: 'yearly',
};

const priorityToApi: Record<TaskPriority, string> = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  urgent: 'urgent',
};

const recurrenceToApi: Record<TaskRecurrence, string> = {
  doesNotRepeat: 'doesNotRepeat',
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  yearly: 'yearly',
};

let cachedTaskStatuses: TaskStatusDefinition[] = [];

export const getCachedTaskStatuses = (): TaskStatusDefinition[] => cachedTaskStatuses;

const parseStatus = (value: unknown): TaskStatus => {
  return String(value ?? '').trim().toLowerCase();
};

const parsePriority = (value: unknown): TaskPriority => {
  if (typeof value === 'number') return priorityByNumber[value] ?? 'medium';
  const normalized = String(value ?? '').toLowerCase();
  if (normalized === 'low') return 'low';
  if (normalized === 'medium') return 'medium';
  if (normalized === 'high') return 'high';
  if (normalized === 'urgent') return 'urgent';
  return 'medium';
};

const parseCategory = (value: unknown): TaskCategoryItem | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const categoryValue = value as { id?: unknown; name?: unknown; code?: unknown; parentCategoryId?: unknown };
  const id = String(categoryValue.id ?? '').trim();
  const name = String(categoryValue.name ?? '').trim();
  const code = String(categoryValue.code ?? '').trim();
  const parentCategoryId = String(categoryValue.parentCategoryId ?? '').trim();
  if (!id || !name) return undefined;
  return {
    id,
    name,
    ...(code ? { code } : {}),
    ...(parentCategoryId ? { parentCategoryId } : {}),
  };
};

const parseTaskLabel = (value: unknown): TaskLabelItem | null => {
  if (!value || typeof value !== 'object') return null;
  const labelValue = value as { id?: unknown; name?: unknown };
  const id = String(labelValue.id ?? '').trim();
  const name = String(labelValue.name ?? '').trim();
  if (!id || !name) return null;
  return { id, name };
};

const normalizeLabelNames = (labels?: string[]): string[] | undefined => {
  if (!Array.isArray(labels)) return undefined;

  const next = labels
    .map((label) => label.trim())
    .filter(Boolean)
    .filter((label, index, source) => source.findIndex((value) => value.toLowerCase() === label.toLowerCase()) === index);

  return next.length > 0 ? next : [];
};

const parseAiRunStatus = (value: unknown): TaskAiRunStatus => {
  const normalized = String(value ?? '').toLowerCase();
  if (normalized === 'queued') return 'queued';
  if (normalized === 'running') return 'running';
  if (normalized === 'succeeded') return 'succeeded';
  if (normalized === 'failed') return 'failed';
  if (normalized === 'needsreview') return 'needsReview';
  return 'none';
};

const parseRecurrence = (value: unknown): TaskRecurrence => {
  if (typeof value === 'number') return recurrenceByNumber[value] ?? 'doesNotRepeat';
  const normalized = String(value ?? '').toLowerCase();
  if (normalized === 'daily') return 'daily';
  if (normalized === 'weekly') return 'weekly';
  if (normalized === 'monthly') return 'monthly';
  if (normalized === 'yearly') return 'yearly';
  return 'doesNotRepeat';
};

const normalizeSubtask = (value: any): SubtaskItem => ({
  id: String(value?.id ?? ''),
  taskKey: String(value?.taskKey ?? ''),
  title: String(value?.title ?? ''),
  status: parseStatus(value?.status),
  assignedToUserId: value?.assignedToUserId ? String(value.assignedToUserId) : undefined,
});

const normalizeConversationLink = (value: any): TaskConversationLinkItem => ({
  conversationId: String(value?.conversationId ?? ''),
  conversationMessageId: value?.conversationMessageId ? String(value.conversationMessageId) : undefined,
  linkedByEmployeeId: String(value?.linkedByEmployeeId ?? ''),
  linkedAt: String(value?.linkedAt ?? ''),
  isPrimary: Boolean(value?.isPrimary),
});

const normalizeRelatedTaskLink = (value: any): TaskRelatedTaskLinkItem => ({
  relatedTaskId: String(value?.relatedTaskId ?? ''),
  relatedTaskKey: String(value?.relatedTaskKey ?? ''),
  relatedTaskTitle: String(value?.relatedTaskTitle ?? ''),
  relatedTaskStatus: parseStatus(value?.relatedTaskStatus),
  linkedByEmployeeId: String(value?.linkedByEmployeeId ?? ''),
  linkedAt: String(value?.linkedAt ?? ''),
});

export const normalizeTask = (value: any): TaskItem => ({
  id: String(value?.id ?? ''),
  title: String(value?.title ?? ''),
  description: value?.description ? String(value.description) : undefined,
  dueDate: value?.dueDate ? String(value.dueDate) : undefined,
  dueTime: value?.dueTime ? String(value.dueTime) : undefined,
  recurrence: parseRecurrence(value?.recurrence),
  priority: parsePriority(value?.priority),
  status: parseStatus(value?.status),
  category: parseCategory(value?.category),
  contactId: value?.contactId ? String(value.contactId) : undefined,
  assetId: value?.assetId ? String(value.assetId) : undefined,
  assignedToUserId: value?.assignedToUserId ? String(value.assignedToUserId) : undefined,
  assignedToAi: Boolean(value?.assignedToAi),
  aiRunStatus: parseAiRunStatus(value?.aiRunStatus),
  aiAttemptCount: Number(value?.aiAttemptCount ?? 0),
  aiLastError: value?.aiLastError ? String(value.aiLastError) : undefined,
  createdBy: String(value?.createdBy ?? ''),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
  taskKey: String(value?.taskKey ?? ''),
  keyNumber: Number(value?.keyNumber ?? 0),
  parentTaskId: value?.parentTaskId ? String(value.parentTaskId) : undefined,
  parentTaskKey: value?.parentTaskKey ? String(value.parentTaskKey) : undefined,
  labels: Array.isArray(value?.labels)
    ? value.labels
      .map((item: unknown) => parseTaskLabel(item))
      .filter((item: TaskLabelItem | null): item is TaskLabelItem => Boolean(item))
    : [],
  subtasks: Array.isArray(value?.subtasks) ? value.subtasks.map(normalizeSubtask) : [],
  conversationLinks: Array.isArray(value?.conversationLinks) ? value.conversationLinks.map(normalizeConversationLink) : [],
  relatedTasks: Array.isArray(value?.relatedTasks) ? value.relatedTasks.map(normalizeRelatedTaskLink) : [],
  visitPlace: normalizeVisitPlace(value?.visitPlace),
});

const normalizeTaskStatusDefinition = (value: any): TaskStatusDefinition | null => {
  const id = String(value?.id ?? '').trim();
  const code = parseStatus(value?.code);
  const name = String(value?.name ?? '').trim();

  if (!id || !code || !name) {
    return null;
  }

  return {
    id,
    code,
    name,
    color: String(value?.color ?? '#6b778c').trim() || '#6b778c',
    displayOrder: Number(value?.displayOrder ?? 0),
    isDefault: Boolean(value?.isDefault),
    isCompleted: Boolean(value?.isCompleted),
    requiresAssignee: Boolean(value?.requiresAssignee),
    transitionToCodes: Array.isArray(value?.transitionToCodes)
      ? value.transitionToCodes.map((item: unknown) => parseStatus(item)).filter(Boolean)
      : [],
  };
};

const normalizeDueTimeForApi = (value?: string): string | undefined => {
  if (!value) return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // HTML time input usually returns HH:mm, while backend TimeSpan binding expects HH:mm:ss.
  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    return `${trimmed}:00`;
  }

  if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  return undefined;
};

const toApiCreateRequest = (request: CreateTaskRequest): Record<string, unknown> => ({
  ...request,
  dueTime: normalizeDueTimeForApi(request.dueTime),
  recurrence: recurrenceToApi[request.recurrence],
  priority: priorityToApi[request.priority],
  categoryId: request.categoryId || undefined,
  labels: normalizeLabelNames(request.labels),
});

const toApiUpdateRequest = (request: UpdateTaskRequest): Record<string, unknown> => ({
  ...request,
  dueTime: request.dueTime !== undefined ? normalizeDueTimeForApi(request.dueTime) : undefined,
  recurrence: request.recurrence !== undefined ? recurrenceToApi[request.recurrence] : undefined,
  priority: request.priority !== undefined ? priorityToApi[request.priority] : undefined,
  status: request.status !== undefined ? parseStatus(request.status) : undefined,
  categoryId: request.categoryId !== undefined ? request.categoryId || undefined : undefined,
  labels: request.labels !== undefined ? normalizeLabelNames(request.labels) : undefined,
});

const toQueryString = (query: TaskListQuery): string => {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.status) params.set('status', parseStatus(query.status));
  if (query.priority) params.set('priority', String(priorityToApi[query.priority]));
  if (query.categoryId) params.set('categoryId', query.categoryId);
  if (query.contactId) params.set('contactId', query.contactId);
  if (query.assetId) params.set('assetId', query.assetId);
  if (query.assignedToUserId) params.set('assignedToUserId', query.assignedToUserId);
  if (query.assignedToAiOnly) params.set('assignedToAiOnly', 'true');
  if (query.unassignedOnly) params.set('unassignedOnly', 'true');
  if (query.dueDateFrom) params.set('dueDateFrom', query.dueDateFrom);
  if (query.dueDateTo) params.set('dueDateTo', query.dueDateTo);
  if (query.searchText) params.set('searchText', query.searchText);
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
};

const pickSingle = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

const pickArray = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) return payload.data;
  return [];
};

const normalizeTaskComment = (value: any): TaskCommentItem => ({
  id: String(value?.id ?? ''),
  taskId: String(value?.taskId ?? ''),
  content: String(value?.content ?? ''),
  createdBy: String(value?.createdBy ?? ''),
  createdAt: String(value?.createdAt ?? ''),
});

const normalizeTaskActivity = (value: any): TaskActivityItem => ({
  id: String(value?.id ?? ''),
  taskId: String(value?.taskId ?? ''),
  type: String(value?.type ?? ''),
  message: String(value?.message ?? ''),
  field: value?.field ? String(value.field) : undefined,
  oldValue: value?.oldValue ? String(value.oldValue) : undefined,
  newValue: value?.newValue ? String(value.newValue) : undefined,
  createdBy: String(value?.createdBy ?? ''),
  createdAt: String(value?.createdAt ?? ''),
});

const normalizeTaskAttachment = (value: any): TaskAttachmentItem => ({
  id: String(value?.id ?? ''),
  taskId: String(value?.taskId ?? ''),
  taskCommentId: value?.taskCommentId ? String(value.taskCommentId) : undefined,
  fileName: String(value?.fileName ?? ''),
  mimeType: String(value?.mimeType ?? ''),
  fileSize: Number(value?.fileSize ?? 0),
  mediaUrl: String(value?.mediaUrl ?? ''),
  createdBy: String(value?.createdBy ?? ''),
  createdAt: String(value?.createdAt ?? ''),
});

export const taskService = {
  listTasks: async (query: TaskListQuery = {}): Promise<PagedTaskResult> => {
    const payload = await apiRequest<any>(`/api/v1/tasks${toQueryString(query)}`);
    const raw = pickSingle(payload);
    const items = Array.isArray(raw?.items) ? raw.items.map(normalizeTask) : [];
    return {
      items,
      totalCount: Number(raw?.totalCount ?? items.length),
      page: Number(raw?.page ?? query.page ?? 1),
      pageSize: Number(raw?.pageSize ?? query.pageSize ?? 25),
    };
  },

  getTaskById: async (taskId: string): Promise<TaskItem | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/tasks/${taskId}`);
      const item = pickSingle(payload);
      return item ? normalizeTask(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  },

  createTask: async (request: CreateTaskRequest): Promise<TaskItem | null> => {
    const payload = await apiRequest<any>('/api/v1/tasks', {
      method: 'POST',
      body: JSON.stringify(toApiCreateRequest(request)),
    });
    const item = pickSingle(payload);
    return item ? normalizeTask(item) : null;
  },

  updateTask: async (taskId: string, request: UpdateTaskRequest): Promise<TaskItem | null> => {
    const payload = await apiRequest<any>(`/api/v1/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(toApiUpdateRequest(request)),
    });
    const item = pickSingle(payload);
    return item ? normalizeTask(item) : null;
  },

  deleteTask: async (taskId: string): Promise<void> => {
    await apiRequest(`/api/v1/tasks/${taskId}`, { method: 'DELETE' });
  },

  unlinkTaskFromConversation: async (taskId: string, conversationId: string): Promise<TaskItem | null> => {
    const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/tasks/${taskId}`, {
      method: 'DELETE',
    });
    const item = pickSingle(payload);
    return item ? normalizeTask(item) : null;
  },

  linkRelatedTask: async (taskId: string, relatedTaskId: string): Promise<TaskItem | null> => {
    const payload = await apiRequest<any>(`/api/v1/tasks/${taskId}/related-tasks`, {
      method: 'POST',
      body: JSON.stringify({ relatedTaskId }),
    });
    const item = pickSingle(payload);
    return item ? normalizeTask(item) : null;
  },

  unlinkRelatedTask: async (taskId: string, relatedTaskId: string): Promise<TaskItem | null> => {
    const payload = await apiRequest<any>(`/api/v1/tasks/${taskId}/related-tasks/${relatedTaskId}`, {
      method: 'DELETE',
    });
    const item = pickSingle(payload);
    return item ? normalizeTask(item) : null;
  },

  createSubtask: async (parentTaskId: string, title: string): Promise<TaskItem | null> => {
    const payload = await apiRequest<any>(`/api/v1/tasks/${parentTaskId}/subtasks`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
    const item = pickSingle(payload);
    return item ? normalizeTask(item) : null;
  },

  listTaskCategories: async (): Promise<TaskCategoryItem[]> => {
    const payload = await apiRequest<any>('/api/v1/tasks/categories');
    const rows = pickArray(payload);
    return rows
      .map((row) => parseCategory(row))
      .filter((category): category is TaskCategoryItem => Boolean(category));
  },

  listTaskLabels: async (): Promise<TaskLabelItem[]> => {
    const payload = await apiRequest<any>('/api/v1/tasks/labels');
    const rows = pickArray(payload);
    return rows
      .map((row) => parseTaskLabel(row))
      .filter((label): label is TaskLabelItem => Boolean(label));
  },

  listTaskStatuses: async (): Promise<TaskStatusDefinition[]> => {
    const payload = await apiRequest<any>('/api/v1/tasks/statuses');
    const rows = pickArray(payload);
    const statuses = rows
      .map((row) => normalizeTaskStatusDefinition(row))
      .filter((status): status is TaskStatusDefinition => Boolean(status));
    cachedTaskStatuses = statuses;
    return statuses;
  },

  createTaskStatus: async (request: {
    name: string;
    code?: string;
    color: string;
    displayOrder?: number;
    isDefault: boolean;
    isCompleted: boolean;
    requiresAssignee: boolean;
    transitionToCodes: string[];
  }): Promise<TaskStatusDefinition | null> => {
    const payload = await apiRequest<any>('/api/v1/tasks/statuses', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    const item = pickSingle(payload);
    return item ? normalizeTaskStatusDefinition(item) : null;
  },

  updateTaskStatus: async (statusId: string, request: {
    name?: string;
    code?: string;
    color?: string;
    displayOrder?: number;
    isDefault?: boolean;
    isCompleted?: boolean;
    requiresAssignee?: boolean;
    transitionToCodes?: string[];
  }): Promise<TaskStatusDefinition | null> => {
    const payload = await apiRequest<any>(`/api/v1/tasks/statuses/${statusId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
    const item = pickSingle(payload);
    return item ? normalizeTaskStatusDefinition(item) : null;
  },

  deleteTaskStatus: async (statusId: string): Promise<void> => {
    await apiRequest(`/api/v1/tasks/statuses/${statusId}`, { method: 'DELETE' });
  },

  createTaskCategory: async (name: string, parentCategoryId?: string): Promise<TaskCategoryItem | null> => {
    const payload = await apiRequest<any>('/api/v1/tasks/categories', {
      method: 'POST',
      body: JSON.stringify({
        name,
        ...(parentCategoryId ? { parentCategoryId } : {}),
      }),
    });
    const item = pickSingle(payload);
    return item ? parseCategory(item) ?? null : null;
  },

  deleteTaskCategory: async (categoryId: string): Promise<void> => {
    await apiRequest(`/api/v1/tasks/categories/${categoryId}`, { method: 'DELETE' });
  },

  listTaskComments: async (taskId: string): Promise<TaskCommentItem[]> => {
    const payload = await apiRequest<any>(`/api/v1/tasks/${taskId}/comments`);
    return pickArray(payload).map(normalizeTaskComment);
  },

  addTaskComment: async (taskId: string, content: string, mentions: TaskCommentMentionInput[] = []): Promise<TaskCommentItem | null> => {
    const payload = await apiRequest<any>(`/api/v1/tasks/${taskId}/comments`, {
      method: 'POST',
      body: JSON.stringify({
        content,
        mentions,
      }),
    });
    const item = pickSingle(payload);
    return item ? normalizeTaskComment(item) : null;
  },

  listTaskActivities: async (taskId: string): Promise<TaskActivityItem[]> => {
    const payload = await apiRequest<any>(`/api/v1/tasks/${taskId}/activity`);
    return pickArray(payload).map(normalizeTaskActivity);
  },

  listTaskAttachments: async (taskId: string): Promise<TaskAttachmentItem[]> => {
    const payload = await apiRequest<any>(`/api/v1/tasks/${taskId}/attachments`);
    return pickArray(payload).map(normalizeTaskAttachment);
  },

  addTaskAttachment: async (taskId: string, file: File, commentId?: string): Promise<TaskAttachmentItem | null> => {
    const formData = new FormData();
    formData.append('file', file);
    if (commentId) {
      formData.append('commentId', commentId);
    }

    const payload = await apiRequest<any>(`/api/v1/tasks/${taskId}/attachments`, {
      method: 'POST',
      body: formData,
    });
    const item = pickSingle(payload);
    return item ? normalizeTaskAttachment(item) : null;
  },

  deleteTaskAttachment: async (taskId: string, attachmentId: string): Promise<void> => {
    await apiRequest(`/api/v1/tasks/${taskId}/attachments/${attachmentId}`, { method: 'DELETE' });
  },
};

export const listTasks = taskService.listTasks;
export const getTaskById = taskService.getTaskById;
export const createTask = taskService.createTask;
export const updateTask = taskService.updateTask;
export const deleteTask = taskService.deleteTask;
export const unlinkTaskFromConversation = taskService.unlinkTaskFromConversation;
export const linkRelatedTask = taskService.linkRelatedTask;
export const unlinkRelatedTask = taskService.unlinkRelatedTask;
export const listTaskCategories = taskService.listTaskCategories;
export const listTaskStatuses = taskService.listTaskStatuses;
export const createTaskStatus = taskService.createTaskStatus;
export const updateTaskStatus = taskService.updateTaskStatus;
export const deleteTaskStatus = taskService.deleteTaskStatus;
export const createTaskCategory = taskService.createTaskCategory;
export const deleteTaskCategory = taskService.deleteTaskCategory;
export const listTaskComments = taskService.listTaskComments;
export const addTaskComment = taskService.addTaskComment;
export const listTaskActivities = taskService.listTaskActivities;
export const listTaskAttachments = taskService.listTaskAttachments;
export const addTaskAttachment = taskService.addTaskAttachment;
export const deleteTaskAttachment = taskService.deleteTaskAttachment;
